import { IBrandTag } from '@faire/web-api--source/faire/brand/tags/IBrandTag';
import { CurrencyUnit } from '@faire/web-api--source/faire/currency/CurrencyUnit';
import { IGeoConstraint } from '@faire/web-api--source/faire/geo/IGeoConstraint';
import { IRetailerRegion } from '@faire/web-api--source/faire/geo/IRetailerRegion';
import { FreeShippingReason } from '@faire/web-api--source/indigofair/data/FreeShippingReason';
import { GuaranteedVolumeStatus } from '@faire/web-api--source/indigofair/data/GuaranteedVolumeStatus';
import { IAddress } from '@faire/web-api--source/indigofair/data/IAddress';
import { IApplicableMembershipBenefits } from '@faire/web-api--source/indigofair/data/IApplicableMembershipBenefits';
import { IBadge } from '@faire/web-api--source/indigofair/data/IBadge';
import { IBrandOrderMinimumOverride } from '@faire/web-api--source/indigofair/data/IBrandOrderMinimumOverride';
import { IBrandPromo } from '@faire/web-api--source/indigofair/data/IBrandPromo';
import { IBrandPromoCollection } from '@faire/web-api--source/indigofair/data/IBrandPromoCollection';
import { IBrandReviewsSummary } from '@faire/web-api--source/indigofair/data/IBrandReviewsSummary';
import { IBrandTilePromoLabel } from '@faire/web-api--source/indigofair/data/IBrandTilePromoLabel';
import { IFaireMoney } from '@faire/web-api--source/indigofair/data/IFaireMoney';
import { IImage } from '@faire/web-api--source/indigofair/data/IImage';
import { IInsiderLabel } from '@faire/web-api--source/indigofair/data/IInsiderLabel';
import { IMembership } from '@faire/web-api--source/indigofair/data/IMembership';
import { IOrderMechanismEnum } from '@faire/web-api--source/indigofair/data/IOrderMechanismEnum';
import { IPayoutTermEnum } from '@faire/web-api--source/indigofair/data/IPayoutTermEnum';
import { IQuote } from '@faire/web-api--source/indigofair/data/IQuote';
import { IShipment } from '@faire/web-api--source/indigofair/data/IShipment';
import { PaymentTerm } from '@faire/web-api--source/indigofair/data/PaymentTerm';
import { TaxFormPreference } from '@faire/web-api--source/indigofair/data/TaxFormPreference';
import { IBusinessIdentifier } from '@faire/web-api--source/indigofair/idv/IBusinessIdentifier';

export namespace IBrand {
  export enum State {
    /**
     * The brand was newly created.
     */
    NEW = 'NEW',
    WAITLISTED = 'WAITLISTED',
    /**
     * THe onboarding form was sent to the brand.
     */
    ONBOARDING_FORM_SENT = 'ONBOARDING_FORM_SENT',
    /**
     * The brand has started the onboarding form.
     */
    ONBOARDING_FORM_STARTED = 'ONBOARDING_FORM_STARTED',
    /**
     * The brand has completed the onboarding form and we have their catalog.
     */
    ONBOARDING_FORM_COMPLETED = 'ONBOARDING_FORM_COMPLETED',
    /**
     * The brand has had at least one product uploaded.
     */
    PROFILE_STARTED = 'PROFILE_STARTED',
    /**
     * The brand has all of their products uploaded.
     */
    ALL_PRODUCTS_UPLOADED = 'ALL_PRODUCTS_UPLOADED',
    PROFILE_IN_DESIGN_REVIEW = 'PROFILE_IN_DESIGN_REVIEW',
    PROFILE_IN_FINAL_REVIEW = 'PROFILE_IN_FINAL_REVIEW',
    PROFILE_ACTIVATED = 'PROFILE_ACTIVATED',
    /**
     * The brand was rejected from Faire.
     * @deprecated
     */
    REJECTED = 'REJECTED',
    PRE_ACTIVATION_REVIEW = 'PRE_ACTIVATION_REVIEW',
    /**
     * Brand is uploading products in self serve
     */
    SELF_SERVE_PRODUCT_UPLOAD = 'SELF_SERVE_PRODUCT_UPLOAD',
    /**
     * Brand has requested assistance in upload products while in self serve
     */
    ASSISTED_UPLOAD_PENDING = 'ASSISTED_UPLOAD_PENDING',
    /**
     * Product catalog is being built by Faire in self serve
     */
    ASSISTED_UPLOAD_STARTED = 'ASSISTED_UPLOAD_STARTED',
    /**
     * Brand is reviewing their store in self serve
     */
    SELF_SERVE_REVIEW = 'SELF_SERVE_REVIEW',
    /**
     * Brand is reviewing their store after requesting self-serve assistance
     */
    ASSISTED_SELF_SERVE_REVIEW = 'ASSISTED_SELF_SERVE_REVIEW',
    /**
     * Brand is being reviewed for fraud by Faire
     */
    FRAUD_REVIEW = 'FRAUD_REVIEW',
    /**
     * Brand has been rejected for fraud
     */
    REJECTED_FOR_FRAUD = 'REJECTED_FOR_FRAUD',
  }

  export enum ReferralRewardsState {
    REFERRAL_REWARDS_STATE_UNKNOWN = 'REFERRAL_REWARDS_STATE_UNKNOWN',
    REWARDS_NOT_APPLICABLE = 'REWARDS_NOT_APPLICABLE',
    REWARDS_PENDING_APPROVAL = 'REWARDS_PENDING_APPROVAL',
    REWARDS_APPROVED = 'REWARDS_APPROVED',
    REWARDS_REJECTED = 'REWARDS_REJECTED',
  }

  export enum FederalTaxClass {
    C_CORPORATION = 'C_CORPORATION',
    S_CORPORATION = 'S_CORPORATION',
    INDIVIDUAL_OR_SOLE_PROPRIETOR = 'INDIVIDUAL_OR_SOLE_PROPRIETOR',
    LIMITED_LIABILITY_COMPANY = 'LIMITED_LIABILITY_COMPANY',
    OTHER = 'OTHER',
  }

  export enum BrandLeadType {
    INBOUND = 'INBOUND',
    OUTBOUND = 'OUTBOUND',
    UNKNOWN = 'UNKNOWN',
  }

  export enum PurchaseOrderBrandType {
    NOT_A_PURCHASE_ORDER_BRAND = 'NOT_A_PURCHASE_ORDER_BRAND',
    WITH_UNCONFIRMED_USER = 'WITH_UNCONFIRMED_USER',
    WITH_CONFIRMED_USER = 'WITH_CONFIRMED_USER',
    WITH_EXISTING_USER = 'WITH_EXISTING_USER',
  }

  export namespace IMinimum {
    /**
     * Return an instance of IMinimum with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (partial?: Partial<IMinimum>): IMinimum =>
      ({
        ...(partial ?? {}),
      }) as IMinimum;
  }

  export interface IMinimum {
    /**
     * @deprecated
     */
    country: string | undefined;
    retailer_region: IRetailerRegion | undefined;
    /**
     * @deprecated
     */
    minimum_order_amount: IFaireMoney | undefined;
    first_order_minimum: IFaireMoney | undefined;
    reorder_minimum: IFaireMoney | undefined;
  }

  /**
   * Apparel team currently only cares about APPAREL & FOOTWEAR & ACCESSORIES,
   * feel free to add more categories if needed.
   */
  export enum PrimaryCategory {
    /**
     * @deprecated
     */
    APPAREL = 'APPAREL',
    /**
     * @deprecated
     */
    FOOTWEAR = 'FOOTWEAR',
    /**
     * @deprecated
     */
    ACCESSORIES = 'ACCESSORIES',
    OTHER_CATEGORY = 'OTHER_CATEGORY',
    /**
     * @deprecated
     */
    KIDS_BABY = 'KIDS_BABY',
    HOME_DECOR = 'HOME_DECOR',
    FOOD_DRINK = 'FOOD_DRINK',
    BEAUTY_WELLNESS = 'BEAUTY_WELLNESS',
    PAPER_NOVELTY = 'PAPER_NOVELTY',
    PETS = 'PETS',
    JEWELRY = 'JEWELRY',
    WOMENS_APPAREL = 'WOMENS_APPAREL',
    WOMENS_ACCESSORIES = 'WOMENS_ACCESSORIES',
    MENS_APPAREL = 'MENS_APPAREL',
    MENS_ACCESSORIES = 'MENS_ACCESSORIES',
    KIDS_BABY_APPAREL = 'KIDS_BABY_APPAREL',
    KIDS_BABY_NON_APPAREL = 'KIDS_BABY_NON_APPAREL',
    RAW_MATERIALS = 'RAW_MATERIALS',
    CBD_THC = 'CBD_THC',
  }

  /**
   * List of certifications for this brand, for hackweek 2020 and populated only by wirefilter
   */
  export enum CertificationType {
    B_CORP = 'B_CORP',
    GOTS = 'GOTS',
    USDA_ORGANIC = 'USDA_ORGANIC',
  }

  /**
   * Size segment is a semi transparent property set on brands populated through a data pipeline
   * this is introduced for the faire direct rewards program and the definitions for each segment can be
   * found here
   * https://www.notion.so/faire/Faire-Direct-Rewards-program-MVP-6bdc27b21c824fd988af029270dfacaf#5fe6f2dbae884b65bcc8875555c2a202
   */
  export enum SizeSegment {
    SIZE_SEGMENT_UNKNOWN = 'SIZE_SEGMENT_UNKNOWN',
    SIZE_SEGMENT_1 = 'SIZE_SEGMENT_1',
    SIZE_SEGMENT_2 = 'SIZE_SEGMENT_2',
    SIZE_SEGMENT_3 = 'SIZE_SEGMENT_3',
    SIZE_SEGMENT_4 = 'SIZE_SEGMENT_4',
  }

  export enum OrderAutoAcceptConfiguration {
    ORDER_AUTO_ACCEPT_CONFIGURATION_UNKNOWN = 'ORDER_AUTO_ACCEPT_CONFIGURATION_UNKNOWN',
    /**
     * [Default] The brand will not automatically accept any new orders.
     */
    DISABLED = 'DISABLED',
    /**
     * The brand will automatically accept new orders from reordering retailers.
     */
    ENABLED_REORDERS = 'ENABLED_REORDERS',
    /**
     * The brand will automatically accept all new orders.
     */
    ENABLED_ALL = 'ENABLED_ALL',
  }

  /**
   * Redacted copy of IBrand (i.e. without redacted fields).
   */
  export type Redacted = Omit<
    IBrand,
    | 'id'
    | 'payout_enabled'
    | 'salesforce_lead_id'
    | 'salesforce_account_id'
    | 'salesforce_owner_id'
    | 'balance_cents'
    | 'payout_fee_bps'
    | 'payout_instrument_token'
    | 'fulfilment_owner_token'
    | 'deranked_categories'
    | 'ships_from'
    | 'commission_bps'
    | 'first_order_commission_bps'
    | 'reduced_commission_bps'
    | 'contact_name'
    | 'email_address'
    | 'phone_number'
    | 'no_resale'
    | 'order_mechanism'
    | 'address'
    | 'fulfilment_notes'
    | 'content_notes'
    | 'order_count'
    | 'restricted_states'
    | 'forbidden_states'
    | 'reduced_commission_states'
    | 'restricted_countries'
    | 'restricted_geo_constraints'
    | 'score'
    | 'alternative_score'
    | 'number_of_stockists'
    | 'ein'
    | 'legal_name'
    | 'federal_tax_class'
    | 'updated_at'
    | 'access_token'
    | 'lead_type'
    | 'balance'
    | 'is_commission_update_abuser'
    | 'default_shipping_address'
    | 'referral_rewards_state'
  >;

  /**
   * Return an instance of IBrand with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (partial?: Partial<IBrand>): IBrand =>
    ({
      categories: [],
      deranked_categories: [],
      images: [],
      social_media_images: [],
      badges: [],
      squared_category_images: {},
      available_terms: [],
      restricted_states: [],
      forbidden_states: [],
      reduced_commission_states: [],
      restricted_countries: [],
      restricted_geo_constraints: [],
      memberships: [],
      story_images: [],
      active_brand_promos: [],
      active_promo_collections: [],
      order_minimum_overrides: [],
      minimums: [],
      certifications: [],
      business_identifiers: [],
      tags: [],
      ...(partial ?? {}),
    }) as IBrand;
}

export interface IBrand {
  token: string | undefined;
  /**
   * Unique identifier of the brand that the brand gets to pick, used in the subdomain for their elevate page
   */
  token_alias: string | undefined;
  /**
   * To be used only internal by the server, not for external APIs.
   * @protobufOption (indigofair.data.redacted) = true
   */
  id: number | undefined; // redacted
  /**
   * If true this Brand is owned by Faire.
   */
  is_internal: boolean | undefined;
  /**
   * Generally all brands should be active. Unless we've determined that a brand has issues
   * that prevent it from being listed on IF. When a brand is inactive, all its products
   * are not displayed to customers, even if the product themselves are active.
   */
  active: boolean | undefined;
  /**
   * @protobufOption (indigofair.data.redacted) = true
   */
  payout_enabled: boolean | undefined; // redacted
  owner_user_token: string | undefined;
  /**
   * If this brand has been mapped to sales force, this is their sales force lead ID.
   * @protobufOption (indigofair.data.redacted) = true
   */
  salesforce_lead_id: string | undefined; // redacted
  /**
   * If this brand has been mapped to sales force, this is their sales force account ID.
   * @protobufOption (indigofair.data.redacted) = true
   */
  salesforce_account_id: string | undefined; // redacted
  /**
   * If this brand is mapped in Sales Force, this stores the owner ID.
   * @protobufOption (indigofair.data.redacted) = true
   */
  salesforce_owner_id: string | undefined; // redacted
  /**
   * The balance of this Brand with Faire.
   * Balance will decrease when brand was paid for items that never arrived or arrived
   * damaged. It will increase when we give credits to a brand.
   * @deprecated
   * @protobufOption (indigofair.data.redacted) = true
   */
  balance_cents: number | undefined; // redacted
  /**
   * The fraction of the total items cost to be used as shipment cost.
   */
  fixed_shipping_cost_bps: number | undefined;
  /**
   * The payout term the owner of this Brand selected to get paid for brand orders.
   */
  payout_term: keyof typeof IPayoutTermEnum.PayoutTerm | undefined;
  /**
   * The payout fee in bps to be used when paying out this Brand.
   * @protobufOption (indigofair.data.redacted) = true
   */
  payout_fee_bps: number | undefined; // redacted
  /**
   * The token of payout instrument to be used when auto-paying for brand orders for this Brand.
   * @protobufOption (indigofair.data.redacted) = true
   */
  payout_instrument_token: string | undefined; // redacted
  /**
   * The token of the employee user token who handles fulfilment for this brand.
   * @protobufOption (indigofair.data.redacted) = true
   */
  fulfilment_owner_token: string | undefined; // redacted
  name: string | undefined;
  short_description: string | undefined;
  description: string | undefined;
  url: string | undefined;
  /**
   * This field is no longer actively populated with brand category information
   * @deprecated
   */
  categories: Array<string>;
  /**
   * These are categories where the brand does poorly so we derank the brand in those categories to
   * preserve relevance.
   * @deprecated
   * @protobufOption (indigofair.data.redacted) = true
   */
  deranked_categories: Array<string>; // redacted
  images: Array<IImage>;
  /**
   * @deprecated
   */
  facebook_handle: string | undefined;
  /**
   * @deprecated
   */
  twitter_handle: string | undefined;
  instagram_handle: string | undefined;
  tiktok_handle: string | undefined;
  /**
   * @deprecated
   */
  youtube_handle: string | undefined;
  /**
   * @deprecated
   */
  pinterest_handle: string | undefined;
  /**
   * This is a derived property from firstOrderMinimumAmountCents and reorderMinimumAmountCents.
   * @deprecated
   */
  minimum_order_amount_cents: number | undefined;
  /**
   * The minimum order amount when a retailer is placing their first order with a brand.
   * @deprecated
   */
  first_order_minimum_amount_cents: number | undefined;
  /**
   * @deprecated
   */
  reorder_minimum_amount_cents: number | undefined;
  /**
   * Optional. This defines where the brand is headquartered. Note that this can be different
   * from both where its products are made and where its products ship from.
   * @deprecated
   */
  from: string | undefined;
  /**
   * Made in information. For products made in the USA, we also specify the state and city,
   * if available. TODO: Move madeIn to an enumerated type.
   */
  made_in: string | undefined;
  made_in_state: string | undefined;
  made_in_city: string | undefined;
  /**
   * This is where the brand is based. Typically where the founders live or are head quartered.
   */
  based_in: string | undefined;
  based_in_state: string | undefined;
  based_in_city: string | undefined;
  /**
   * The country where a brand last shipped from or most likely ships from (ISO 3166 Alpha 3).
   */
  ships_from_country: string | undefined;
  /**
   * The country where a brand tells us they ship from (ISO 3166 Alpha 3).
   * If provided, this will determine shipsFromCountry. If not, we try to infer shipsFromCountry.
   * @protobufOption (indigofair.data.redacted) = true
   */
  ships_from: string | undefined; // redacted
  /**
   * Defines whether a Brand is in a "core" country, where core is defined as
   * a country we've enabled local retailer support for. Brands in these
   * countries have access to Faire Direct.
   * @deprecated
   */
  is_in_core_country: boolean | undefined;
  /**
   * Defines whether a Brand is eligible for Insider Subsidy enrollment.
   */
  is_eligible_for_insider_subsidy: boolean | undefined;
  /**
   * The brand is made using materials or techniques good for the environment. If null, unknown.
   *  Superseded by eco-friendly brand tag
   * @deprecated
   */
  eco_friendly: boolean | undefined;
  /**
   * The brand dedicates some of its proceeds to a charitable effort. If null, unknown.
   *  Superseded by social good brand tag
   * @deprecated
   */
  charitable: boolean | undefined;
  /**
   * The brand is hand made. If null, unknown. Superseded by handmade brand tag
   * @deprecated
   */
  hand_made: boolean | undefined;
  /**
   * The brand is women owned. If null, unknown. Superseded by women owned brand tag
   * @deprecated
   */
  women_owned: boolean | undefined;
  /**
   * The brand's products are organic. If null, unknown. Superseded by organic brand tag
   * @deprecated
   */
  organic: boolean | undefined;
  /**
   * The brand's products are small batch. If null, unknown. Superseded by small batch brand tag
   * @deprecated
   */
  small_batch: boolean | undefined;
  /**
   * Whether the brand is sold on Amazon, either by the brand itself or third parties.
   *  Superseded by not sold on amazon brand tag
   * @deprecated
   */
  sold_on_amazon: boolean | undefined;
  /**
   * Read-only (generated by the server).
   */
  made_in_usa_description: string | undefined;
  /**
   * Read-only (generated by the server).
   */
  hand_made_description: string | undefined;
  /**
   * Writable. If the maker is charitable, what kind of charity do they do?
   */
  charitable_description: string | undefined;
  /**
   * Writable. If the maker is eco friendly, what is eco friendly about it?
   */
  eco_friendly_description: string | undefined;
  /**
   * The brand is local to the location of the querying retailer. If null, unknown.
   */
  local: boolean | undefined;
  /**
   * A quote from the owner of this brand.
   */
  owner_quote: IQuote | undefined;
  /**
   * Deprecated in favor of profileImage
   * @deprecated
   */
  story_image: IImage | undefined;
  /**
   * A url that links to either a Vimeo or a YouTube video.
   */
  video_url: string | undefined;
  /**
   * A list of instagram images.
   */
  social_media_images: Array<IImage>;
  badges: Array<IBadge>;
  /**
   * Some brands have intrinsic lead times (even when not back-ordered).
   * Handmade brands for example can take four weeks to fulfil.
   * @deprecated
   */
  lead_time_weeks: number | undefined;
  /**
   * The average lead time for this brand.
   */
  lead_time_days: number | undefined;
  /**
   * When displaying a range for the lead time, this is the lower bound of that range.
   */
  lower_bound_lead_time_days: number | undefined;
  /**
   * When displaying a range for the lead time, this is the upper bound of that range.
   */
  upper_bound_lead_time_days: number | undefined;
  /**
   * Whether the current customer has favorited this brand. Replaced with `isFollowing`
   * @deprecated
   */
  favorite: boolean | undefined;
  /**
   * Whether the current customer is following the brand
   */
  is_following: boolean | undefined;
  /**
   * This is a summarized view of the brand's likes or followers across all of its social media
   * platforms.
   */
  likes: number | undefined;
  /**
   * Best guess on when the brand was created. Sometimes approximate.
   */
  creation_year: number | undefined;
  /**
   * A squared image representing the brand to be used in tiles and in the app.
   */
  squared_image: IImage | undefined;
  /**
   * The best image to display for a brand/category pair.
   */
  squared_category_images: Partial<Record<string, IImage>>;
  /**
   * Use squaredImage instead.
   * @deprecated
   */
  email_hero_image: IImage | undefined;
  email_description: string | undefined;
  marketing_email_subject: string | undefined;
  /**
   * These fields have to do with the logistics and fulfilment aspects of a brand.
   * @protobufOption (indigofair.data.redacted) = true
   */
  commission_bps: number | undefined; // redacted
  /**
   * This commission rate applies for the first order by a retailer for a brand.
   * @protobufOption (indigofair.data.redacted) = true
   */
  first_order_commission_bps: number | undefined; // redacted
  /**
   * If not null, this commission applies in states where we give the brand reduced commission.
   * @protobufOption (indigofair.data.redacted) = true
   */
  reduced_commission_bps: number | undefined; // redacted
  /**
   * @protobufOption (indigofair.data.redacted) = true
   */
  contact_name: string | undefined; // redacted
  /**
   * @protobufOption (indigofair.data.redacted) = true
   */
  email_address: string | undefined; // redacted
  /**
   * @protobufOption (indigofair.data.redacted) = true
   */
  phone_number: string | undefined; // redacted
  accepted_terms: boolean | undefined;
  /**
   * The brand has opted out of the chat feature.
   */
  hide_chat: boolean | undefined;
  /**
   * The brand does not want us to resell their products. Flash-sales cannot be created.
   * Their products cannot be accepted in inventory. Returns are shipped to them.
   * @protobufOption (indigofair.data.redacted) = true
   */
  no_resale: boolean | undefined; // redacted
  /**
   * Order mechanism allows us to know how a certain brand is fulfilled. We started with MANUAL
   * fulfillment for all brands and we will add new mechanisms as we automate fulfilment.
   * @protobufOption (indigofair.data.redacted) = true
   */
  order_mechanism: keyof typeof IOrderMechanismEnum.OrderMechanism | undefined; // redacted
  /**
   * The address where this brand ships products from.
   * @protobufOption (indigofair.data.redacted) = true
   */
  address: IAddress | undefined; // redacted
  /**
   * Number of followers across the main social networks.
   */
  facebook_followers: number | undefined;
  instagram_followers: number | undefined;
  twitter_followers: number | undefined;
  pinterest_followers: number | undefined;
  /**
   * If not null, this is a reward amount to show for this brand.
   * @deprecated
   */
  first_order_reward_amount_cents: number | undefined;
  /**
   * If not null, this is the default amount that will be applied if the retailer is eligible.
   * @deprecated
   */
  default_reward_amount_cents: number | undefined;
  /**
   * If not null, this is a discount percentage to show for this brand.
   */
  first_order_discount_percentage: number | undefined;
  /**
   * If not null, this is the default discount for this brand, if the retailer is eligible.
   */
  default_discount_percentage: number | undefined;
  /**
   * If not empty, this is the list of terms available for this brand and the current retailer.
   * Empty is there is no retailer in the session, the frontend must re-fetch this brand before
   * going through flows that require this information.
   * @deprecated
   */
  available_terms: Array<keyof typeof PaymentTerm>;
  /**
   * Notes on the brand to keep track of fulfilment information.
   * @protobufOption (indigofair.data.redacted) = true
   */
  fulfilment_notes: string | undefined; // redacted
  /**
   * Notes on the brand to keep track of information about the content associated with this
   * brand. This is used as a channel of communication between Upworkers/CF and our content team.
   * @protobufOption (indigofair.data.redacted) = true
   */
  content_notes: string | undefined; // redacted
  /**
   * The number of times this brand was ordered.
   * @protobufOption (indigofair.data.redacted) = true
   */
  order_count: number | undefined; // redacted
  /**
   * Optional product collection containing starter items for this brand.
   */
  starter_collection_token: string | undefined;
  /**
   * The brand asked us not to approach retailers in these states.
   * @deprecated
   * @protobufOption (indigofair.data.redacted) = true
   */
  restricted_states: Array<string>; // redacted
  /**
   * @protobufOption (indigofair.data.redacted) = true
   */
  forbidden_states: Array<string>; // redacted
  /**
   * @protobufOption (indigofair.data.redacted) = true
   */
  reduced_commission_states: Array<string>; // redacted
  forbids_online_only_retailers: boolean | undefined;
  forbids_social_media_only_retailers: boolean | undefined;
  /**
   * The brand asked to not sell to retailers based in this list of countries.
   * @deprecated
   * @protobufOption (indigofair.data.redacted) = true
   */
  restricted_countries: Array<string>; // redacted
  /**
   * The brand asked to not sell to retailers based in this list of geo constraints.
   * @protobufOption (indigofair.data.redacted) = true
   */
  restricted_geo_constraints: Array<IGeoConstraint>; // redacted
  /**
   * Is this the first order for this brand and retailer?
   */
  is_first_order: boolean | undefined;
  /**
   * Maximum amount that can be ordered on first order with free returns for this brand.
   * @deprecated
   */
  first_order_returns_limit_cents: number | undefined;
  zip_code_protection_enabled: boolean | undefined;
  /**
   * The scores that we use to sort brands in the marketplace.
   * @protobufOption (indigofair.data.redacted) = true
   */
  score: number | undefined; // redacted
  /**
   * @protobufOption (indigofair.data.redacted) = true
   */
  alternative_score: number | undefined; // redacted
  first_active_at: number | undefined;
  active_products_count: number | undefined;
  last_product_added_at: number | undefined;
  has_new_products: boolean | undefined;
  memberships: Array<IMembership>;
  /**
   * No longer in use. Refer to logic in Comissions
   * @deprecated
   */
  elevate_commission_bps: number | undefined;
  /**
   * If the brand is within the Boom exclusivity window and the retailer in the current session
   * has boom, this flag is true.
   */
  is_boom_exclusive: boolean | undefined;
  /**
   * If the brand allows scheduled orders or not
   */
  allows_scheduled_orders: boolean | undefined;
  /**
   * Whether the brand is "new" for some reasonable definition of new to mean "newly added".
   */
  is_new: boolean | undefined;
  /**
   * If this flag is set, the lead time estimates are not computed automatically.
   */
  override_lead_time_estimates: boolean | undefined;
  vacation_start_date: number | undefined;
  vacation_end_date: number | undefined;
  vacation_banner_text: string | undefined;
  /**
   * The number of retailers that cary this brand.
   * @protobufOption (indigofair.data.redacted) = true
   */
  number_of_stockists: number | undefined; // redacted
  /**
   * ein, legalName, and federalTaxClass are three fields that we are collecting
   * (for legal reasons) from our US makers. If any of these fields are null (for US makers),
   * FederalTaxInfoNeeded should be set to true.
   * @protobufOption (indigofair.data.redacted) = true
   */
  ein: string | undefined; // redacted
  /**
   * @protobufOption (indigofair.data.redacted) = true
   */
  legal_name: string | undefined; // redacted
  /**
   * @protobufOption (indigofair.data.redacted) = true
   */
  federal_tax_class: keyof typeof IBrand.FederalTaxClass | undefined; // redacted
  federal_tax_info_needed: boolean | undefined;
  /**
   * @deprecated
   */
  no_consumer_resale: boolean | undefined;
  /**
   * Whether the retailer is opted in to the Maker buyback program.
   * When enrolled, returns from retailers are charged + sent to the maker.
   */
  accepts_returns: boolean | undefined;
  /**
   * Discount applied when charging the maker for a retailer's returned orders.
   */
  return_orders_discount_bps: number | undefined;
  brand_reviews_summary: IBrandReviewsSummary | undefined;
  version: number | undefined;
  created_at: number | undefined;
  /**
   * @protobufOption (indigofair.data.redacted) = true
   */
  updated_at: number | undefined; // redacted
  can_set_packaging_and_handling_costs: boolean | undefined;
  shipping_label_printing_preference:
    | keyof typeof IShipment.ShippingLabelPrintingPreference
    | undefined;
  state: keyof typeof IBrand.State | undefined;
  owner_first_name: string | undefined;
  owner_last_name: string | undefined;
  /**
   * A banner image which is shown on header of a brand's page.
   */
  banner_image: IImage | undefined;
  /**
   * A round image, typically of the owner shown close to the owner's quote.
   */
  profile_image: IImage | undefined;
  story_images: Array<IImage>;
  /**
   * Not to be used on the client.
   * @protobufOption (indigofair.data.redacted) = true
   */
  access_token: string | undefined; // redacted
  selected_for_international_best_sellers: boolean | undefined;
  /**
   * If the viewing retailer gets free shipping for a brand based on their current or eligible Insider status,
   * this will be populated with the brand-specific FreeShippingReason.
   * Note that this should NOT be used to determine free shipping at checkout since it will be populated for retailers
   * who have been invited to but have not signed up for Insider.
   * For now we only support SELECT_FREE_SHIPPING though we will likely support others later on.
   */
  free_shipping_reason: keyof typeof FreeShippingReason | undefined;
  /**
   * All insider benefits a viewing retailer gets for this brand based on their current or eligible Insider status.
   */
  insider_benefits: IApplicableMembershipBenefits | undefined;
  insider_label: IInsiderLabel | undefined;
  /**
   * @deprecated
   */
  active_brand_promos: Array<IBrandPromo>;
  /**
   * Replaced by activePromoCollections
   * @deprecated
   */
  active_promo_collection: IBrandPromoCollection | undefined;
  active_promo_collections: Array<IBrandPromoCollection>;
  /**
   * @protobufOption (indigofair.data.redacted) = true
   */
  lead_type: keyof typeof IBrand.BrandLeadType | undefined; // redacted
  purchase_order_brand_type:
    | keyof typeof IBrand.PurchaseOrderBrandType
    | undefined;
  purchase_order_brand_lead_token: string | undefined;
  /**
   * Total commission is calculated as a sum of base cents and total cost fraction based on commission bps
   * @deprecated
   */
  commission_base_cents: number | undefined;
  /**
   * @deprecated
   */
  first_order_commission_base_cents: number | undefined;
  /**
   * Forcibly enable faire direct access for this brand
   */
  force_enable_faire_direct: boolean | undefined;
  /**
   * replaced with indigofair.data.GetBrandResponse.orderMinimumOverrides, and thus only available in admin2
   * @deprecated
   */
  order_minimum_overrides: Array<IBrandOrderMinimumOverride>;
  /**
   * Whether to enable prepacks (which are specific to apparel) in maker portal product editor
   */
  enable_pre_packs: boolean | undefined;
  /**
   * A logo image to be used throughout the experience
   */
  logo_image: IImage | undefined;
  brand_lead_token: string | undefined;
  /**
   * @protobufOption (indigofair.data.redacted) = true
   */
  balance: IFaireMoney | undefined; // redacted
  /**
   * The minimum that applies to orders from the current retailer to this brand.
   * Only makes sense for retailers and logged-out users. For brands, use firstOrderMinimumAmount and
   * reorderMinimumAmount.
   */
  minimum_order_amount: IFaireMoney | undefined;
  /**
   * The minimum order amount when a retailer is placing their first order with this brand.
   * For retailers and logged-out users, prefer minimumOrderAmount, as it takes into account whether the current
   * retailer has already ordered from the brand, or has an order minimum override.
   */
  first_order_minimum_amount: IFaireMoney | undefined;
  /**
   * The minimum order amount when a retailer is reordering from this brand.
   * For retailers and logged-out users, prefer minimumOrderAmount, as it takes into account whether the current
   * retailer has already ordered from the brand, or has an order minimum override.
   */
  reorder_minimum_amount: IFaireMoney | undefined;
  /**
   * If not null, this is a reward amount to show for this brand.
   */
  first_order_reward_amount: IFaireMoney | undefined;
  /**
   * Maximum amount that can be ordered on first order with free returns for this brand.
   */
  first_order_returns_limit: IFaireMoney | undefined;
  /**
   * Total commission is calculated as a sum of base cents and total cost fraction based on commission bps
   */
  commission_base: IFaireMoney | undefined;
  first_order_commission_base: IFaireMoney | undefined;
  /**
   * All of a brand's region-specific minimums, potentially excluding the USD minimum.
   * Note that this field is redacted for all users except for Faire employees and this brand's owners.
   * TODO(FD-60525) Update this comment once the brand_minimums table is backfilled with all USD minimums.
   */
  minimums: Array<IBrand.IMinimum>;
  /**
   * @protobufOption (indigofair.data.redacted) = true
   */
  is_commission_update_abuser: boolean | undefined; // redacted
  /**
   * @deprecated
   */
  can_update_accepts_returns: boolean | undefined;
  /**
   * Timestamp when the user accepted the Buyback program terms, or null if they have
   * not accepted them. Terms need to be accepted to be able to accept returns.
   */
  accepted_buyback_terms_at: number | undefined;
  /**
   * Determines if we will show the maker the ability to update their maker buyback settings
   * Some conditions apply, e.g. an american default return address, payout terms aren't CC, etc.
   */
  show_maker_buyback_setting: boolean | undefined;
  /**
   * Determines if we will show the full maker buyback enrolment banner
   * (i.e. the maker cannot quickly toggle between on/off and needs to complete some information)
   */
  show_maker_buyback_enrol_banner: boolean | undefined;
  brand_size_chart: IImage | undefined;
  live_streaming_enabled: boolean | undefined;
  guaranteed_volume_status: keyof typeof GuaranteedVolumeStatus | undefined;
  /**
   * True when the brand has a sample product and offerSamples is true
   * @deprecated
   */
  has_samples: boolean | undefined;
  /**
   * Ships from address, sent only to brand portal
   */
  ships_from_address: IAddress | undefined;
  /**
   * The expected time it'll take orders to ship from this brand to the logged-in retailer's best address
   */
  shipping_time_lower_bound_days: number | undefined;
  shipping_time_upper_bound_days: number | undefined;
  estimated_ship_date_formatted: string | undefined;
  primary_category: keyof typeof IBrand.PrimaryCategory | undefined;
  /**
   * @deprecated
   */
  certifications: Array<keyof typeof IBrand.CertificationType>;
  is_apparel: boolean | undefined;
  /**
   * @deprecated
   */
  faire_winter_market_apparel_preorder_special_eligibility: boolean | undefined;
  /**
   * Used to display discount matching on mobile during FSM/FWM.
   */
  brand_promo_string: string | undefined;
  /**
   * Used to display featured discount promo.
   * figma: https://www.figma.com/file/qKnG7kRLCcvrn1rVtqYynz/FRA%2FDEU--Local-discount?node-id=377%3A125361
   */
  featured_brand_promo_label: IBrandTilePromoLabel | undefined;
  brand_promo_label: IBrandTilePromoLabel | undefined;
  business_identifiers: Array<IBusinessIdentifier>;
  /**
   * @deprecated
   */
  special_collection_threshold_amount_cents: IFaireMoney | undefined;
  /**
   * @deprecated
   */
  loyalty_program_enrolled: boolean | undefined;
  original_locale_id: string | undefined;
  /**
   * Deprecated, please use payoutInstrumentLink.adyenPayoutCurrency
   * @deprecated
   */
  currency: string | undefined;
  /**
   * The brand's chosen currency.
   * This currency is the base currency for the brand's product catalog as well as the currency all money should be
   * displayed in for the brand portal.
   * Note that this is distinct from the brand's payout currency; see payoutInstrumentLink.adyenPayoutCurrency.
   */
  shop_currency: keyof typeof CurrencyUnit | undefined;
  /**
   * isHighlightedBrandForPromotionalEvent is used to highlight brands during a promotional event.
   * This will mirror the behavior used during FWM 2021 in faireWinterMarketApparelPreorderSpecialEligibility,
   * which highlighted apparel preorder brands. For the FSM 2021, this will be used to highlight international brands.
   */
  is_highlighted_brand_for_promotional_event: boolean | undefined;
  size_segment: keyof typeof IBrand.SizeSegment | undefined;
  /**
   * Maps to DbBrand.defaultShippingAddress. Make sure you want this vs shipsFromAddress.
   * @protobufOption (indigofair.data.redacted) = true
   */
  default_shipping_address: IAddress | undefined; // redacted
  /**
   * True if the brand qualifies as a Top Shop for the current cycle
   */
  is_top_shop: boolean | undefined;
  /**
   * Tax Form Preference is used to store a brand's preference on how they would like to receive the 1099K tax forms
   */
  tax_form_preference: keyof typeof TaxFormPreference | undefined;
  /**
   * Timestamp reflecting the last point of time at which a brand changed their tax form preference
   */
  tax_form_preference_last_changed_at: number | undefined;
  tags: Array<IBrandTag>;
  /**
   * @protobufOption (indigofair.data.redacted) = true
   */
  referral_rewards_state: keyof typeof IBrand.ReferralRewardsState | undefined; // redacted
  /**
   * True if the brand qualifies as a showroom brand
   */
  is_showroom: boolean | undefined;
  is_mid_price_point_plus: boolean | undefined;
  published_products_count: number | undefined;
  brand_exclusivity_enabled: boolean | undefined;
  order_auto_accept_configuration:
    | keyof typeof IBrand.OrderAutoAcceptConfiguration
    | undefined;
}
